import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowLeft, ArrowUpRight, ShieldCheck } from 'lucide-react';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, ButtonLink } from '@components/common/Button';
import { ClientDetailsFields } from '@components/checkout/ClientDetailsFields';
import { LegalAcceptanceFields } from '@components/checkout/LegalAcceptanceFields';
import { Container } from '@components/common/Container';
import { EditorialHeader, Overline, PageSection, TextLink } from '@components/common/Editorial';
import { Price } from '@components/product/Price';
import { siteConfig } from '@config/site';
import { getProductBySlug } from '@data/products';
import { usePageTitle } from '@hooks/usePageTitle';
import { resolveCheckoutSelection, validateAcceptance, validateClientDetails } from '@services/checkout/checkoutIntent';
import { startCheckout } from '@services/checkout/clientAcceptance';
import { createRequestId, focusFirstInvalid } from '@services/checkout/requestId';
export const CheckoutPage = () => {
    const { t, i18n } = useTranslation();
    const [params] = useSearchParams();
    const requestedSlug = params.get('product');
    const requestedProduct = requestedSlug ? getProductBySlug(requestedSlug) : undefined;
    const selection = resolveCheckoutSelection(params.get('product'), params.get('variant'));
    const [terms, setTerms] = useState(false);
    const [service, setService] = useState(false);
    const [digital, setDigital] = useState(false);
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deliveryError, setDeliveryError] = useState(false);
    const requestId = useRef(createRequestId());
    const submissionLock = useRef(false);
    const formRef = useRef(null);
    usePageTitle('checkout.title');
    if (requestedProduct?.availability === 'coming_soon') {
        return (_jsx(EditorialHeader, { eyebrow: t('availability.comingSoon'), title: t(requestedProduct.nameKey), intro: t('availability.checkoutUnavailable'), backTo: "/products", backLabel: t('interior.product.back'), children: _jsx(ButtonLink, { to: `/products/${requestedProduct.slug}`, children: t('actions.viewDetails') }) }));
    }
    if (!selection) {
        return (_jsx(EditorialHeader, { eyebrow: t('checkout.eyebrow'), title: t('checkout.invalidTitle'), intro: t('checkout.invalidText'), backTo: "/products", backLabel: t('interior.product.back') }));
    }
    const errors = validateAcceptance(selection.product.fulfilmentType, {
        termsAccepted: terms,
        immediateServiceAccepted: service,
        immediateDigitalAccepted: digital
    });
    const client = { fullName, email, phone };
    const clientErrors = validateClientDetails(client);
    const hasErrors = Object.values(errors).some(Boolean) || Object.values(clientErrors).some(Boolean);
    const submit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        setDeliveryError(false);
        if (hasErrors) {
            focusFirstInvalid(formRef.current);
            return;
        }
        if (submissionLock.current)
            return;
        const acceptance = {
            termsAccepted: terms,
            immediateServiceAccepted: service,
            immediateDigitalAccepted: digital
        };
        submissionLock.current = true;
        setSubmitting(true);
        try {
            const checkout = await startCheckout({
                requestId: requestId.current,
                client: {
                    fullName: fullName.trim(),
                    email: email.trim(),
                    phone: phone.trim()
                },
                acceptance,
                productId: selection.product.id,
                ...(selection.variantId ? { planId: selection.variantId } : {}),
                ...(selection.durationMonths ? { duration: selection.durationMonths } : {}),
                locale: i18n.resolvedLanguage ?? i18n.language
            });
            window.location.assign(checkout.checkoutUrl);
        }
        catch {
            setDeliveryError(true);
        }
        finally {
            submissionLock.current = false;
            setSubmitting(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(EditorialHeader, { eyebrow: t('checkout.eyebrow'), title: t('checkout.title'), intro: t('checkout.intro'), backTo: `/products/${selection.product.slug}`, backLabel: t('checkout.back') }), _jsx(PageSection, { children: _jsxs(CheckoutGrid, { children: [_jsxs("form", { ref: formRef, onSubmit: submit, noValidate: true, children: [_jsx(Overline, { children: t('checkout.acceptancesTitle') }), _jsx(ClientDetailsFields, { idPrefix: "checkout", values: client, errors: clientErrors, submitted: submitted, onChange: (field, value) => {
                                        if (field === 'fullName')
                                            setFullName(value);
                                        if (field === 'email')
                                            setEmail(value);
                                        if (field === 'phone')
                                            setPhone(value);
                                    } }), _jsxs(PrivacyInfo, { children: [_jsx(ShieldCheck, { size: 22, "aria-hidden": "true" }), _jsx("p", { children: _jsx(Trans, { i18nKey: "checkout.privacyInfo", components: { privacy: _jsx(Link, { to: "/privacy" }) } }) })] }), _jsx(LegalAcceptanceFields, { idPrefix: "checkout", secondaryKind: selection.product.fulfilmentType, termsAccepted: terms, secondaryAccepted: selection.product.fulfilmentType === 'service' ? service : digital, submitted: submitted, termsError: errors.terms, secondaryError: selection.product.fulfilmentType === 'service'
                                        ? errors.immediateService
                                        : errors.immediateDigital, onTermsChange: setTerms, onSecondaryChange: selection.product.fulfilmentType === 'service' ? setService : setDigital }), _jsxs(SubmitButton, { type: "submit", disabled: submitting, children: [t(submitting ? 'checkout.sending' : 'checkout.continuePayment'), _jsx(ArrowUpRight, { size: 17, "aria-hidden": "true" })] }), submitted && hasErrors && _jsx(Error, { role: "alert", children: t('checkout.formError') }), deliveryError && _jsx(Error, { role: "alert", children: t('checkout.deliveryError') })] }), _jsxs(Summary, { "aria-labelledby": "checkout-summary-title", children: [_jsx(Overline, { children: t('checkout.summary') }), _jsx("h2", { id: "checkout-summary-title", children: t(selection.product.nameKey) }), selection.durationMonths && (_jsx("p", { children: t('personalCare360.duration', { months: selection.durationMonths }) })), _jsxs(Total, { children: [_jsx("span", { children: t('tax.subtotalExcluding') }), _jsx("strong", { children: _jsx(Price, { amountMinor: selection.product.priceMinor, currency: selection.product.currency }) })] }), _jsxs(Seller, { children: [_jsx("strong", { children: t('checkout.seller') }), _jsx("span", { children: siteConfig.legalName }), _jsxs("span", { children: ["NIF ", siteConfig.taxId] }), _jsx("span", { children: siteConfig.registeredAddress }), _jsx("a", { href: `mailto:${siteConfig.email}`, children: siteConfig.email })] }), _jsxs(TextLink, { to: `/products/${selection.product.slug}`, children: [_jsx(ArrowLeft, { size: 16, "aria-hidden": "true" }), t('checkout.back')] })] })] }) })] }));
};
const CheckoutGrid = styled(Container) `
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.75fr);
  gap: clamp(2rem, 7vw, 6rem);
  align-items: start;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;
const PrivacyInfo = styled.div `
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.8rem;
  margin: 1rem 0 1.5rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
    line-height: 1.65;
  }
  a {
    color: ${({ theme }) => theme.colors.navy900};
    text-decoration: underline;
  }
`;
const Error = styled.p `
  margin: 0.65rem 0 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.875rem;
  line-height: 1.55;
`;
const SubmitButton = styled(Button) `
  margin-top: 1.5rem;
  width: 100%;
  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;
const Summary = styled.aside `
  position: sticky;
  top: 8rem;
  border-top: 2px solid ${({ theme }) => theme.colors.navy900};
  padding-top: 1.25rem;
  h2 {
    margin: 0.8rem 0 0.35rem;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.6rem;
  }
  > p {
    margin: 0;
    color: ${({ theme }) => theme.colors.muted};
  }
  @media (max-width: 800px) {
    position: static;
  }
`;
const Total = styled.div `
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  padding-top: 1rem;
  strong {
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 1.5rem;
  }
`;
const Seller = styled.div `
  display: grid;
  gap: 0.25rem;
  margin: 1.5rem 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.8125rem;
  line-height: 1.55;
  strong {
    color: ${({ theme }) => theme.colors.navy900};
  }
  a {
    color: ${({ theme }) => theme.colors.navy900};
    text-decoration: underline;
  }
`;
